import { useFormContext } from 'react-hook-form';
import { Checkbox, Multiselect } from '~/components';
import type { FormTypography } from '~/components/forms/FormLabel';
import { OptionType } from '~/features/types';
import { useSubspecialtyOptions } from '../hooks/useSubspecialties';
import { GENERAL_WORK_REQUIRED_LABEL } from '../subspecialties';

type SubspecialtyMultiselectProps = {
  specialtyId: string | undefined;
  label: string;
  cyTag: string;
  typography?: FormTypography;
  disabled?: boolean;
};

const SubspecialtyMultiselect = ({
  specialtyId,
  label,
  cyTag,
  typography,
  disabled,
}: SubspecialtyMultiselectProps) => {
  const options = useSubspecialtyOptions(specialtyId);
  return (
    <Multiselect
      name="subspecialties"
      cyTag={cyTag}
      label={label}
      options={options}
      isClearable
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      typography={typography}
      disabled={disabled}
    />
  );
};

type SubspecialtyFieldsProps = {
  specialtyId: string;
  subspecialtiesCyTag: string;
  generalSpecialtyCyTag: string;
};

/**
 * Optional subspecialties under the chosen specialty, and -- only once one is
 * picked -- the "General work required" box. Render it only when a specialty
 * is chosen. The form must have `subspecialties` and `generalSpecialtyFlag`.
 */
export const SubspecialtyFields = ({
  specialtyId,
  subspecialtiesCyTag,
  generalSpecialtyCyTag,
}: SubspecialtyFieldsProps) => {
  const { watch } = useFormContext();
  const selected: OptionType[] | undefined = watch('subspecialties');

  return (
    <div className="flex flex-col gap-5">
      <SubspecialtyMultiselect
        specialtyId={specialtyId}
        label="Subspecialties (optional)"
        cyTag={subspecialtiesCyTag}
      />
      {selected && selected.length > 0 && (
        <Checkbox
          name="generalSpecialtyFlag"
          cyTag={generalSpecialtyCyTag}
          label={GENERAL_WORK_REQUIRED_LABEL}
        />
      )}
    </div>
  );
};

/**
 * The search-filter version: any of the picked subspecialties matches. Render it
 * only when a specialty filter is chosen. The form must have `aoiSpecialty` and
 * `subspecialties`.
 */
export const SubspecialtyFilter = ({
  cyTag,
  typography,
  disabled,
}: {
  cyTag: string;
  typography?: FormTypography;
  disabled?: boolean;
}) => {
  const { watch } = useFormContext();
  const specialtyId: string | undefined = watch('aoiSpecialty')?.value;
  return (
    <SubspecialtyMultiselect
      specialtyId={specialtyId}
      label="Subspecialties"
      cyTag={cyTag}
      typography={typography}
      disabled={disabled}
    />
  );
};
